// leaderboard-controller.ts — the native in-game LEADERBOARD screen, LOGIC half (pure TS, no DOM).
// Fetches the ranked-player JSON the wi-api worker serves (GET /stats/leaderboard.json +
// /stats/profile.json — see workers/leaderboard/src/players.ts) and exposes it as screen state the
// view (leaderboard-view.ts) renders. Modeled on dom/lobby-controller.ts's fetchServers seam: an
// injected httpGet keeps the whole controller node-testable with a scripted service.
//
// Replaces the old behavior where the LEADERBOARD menu button opened the server-rendered HTML page
// in a new tab (Shell.cpp HostInitiateWebView) — now it's a native WI-styled screen.

import { queryEncode } from '../ui/loginhandler.ts';
import { kszReplayViewerUrl, replayViewerUrl, openReplayViewer } from '../ui/replaylink.ts';

export { kszReplayViewerUrl };

/** A ranked leaderboard row (mirrors PlayerListRow in workers/leaderboard/src/players.ts). */
export interface LeaderRow {
  rank: number;
  name: string;
  rating: number;
  games: number;
  wins: number;
  winRate: number;
  kd: number;
  playtimeSecs: number;
  lastGameUtc: number;
  avatar: string | null;
  isBot: boolean; // a rated AI opponent (ApexBot) — rendered with a badge
}

/** A full player profile (mirrors PlayerProfile in workers/leaderboard/src/players.ts). */
export interface LeaderProfile {
  name: string;
  rating: number;
  games: number;
  wins: number;
  losses: number;
  winRate: number;
  avatar: string | null;
  isBot: boolean; // a rated AI opponent (ApexBot)
  createdUtc: number | null;
  lastGameUtc: number | null;
  combat: {
    creditsAcquired: number;
    creditsConsumed: number;
    unitsKilled: number;
    unitsLost: number;
    structsKilled: number;
    structsLost: number;
    kd: number;
    playtimeSecs: number;
    built: number;
  };
  activity: Array<{ month: string; games: number }>;
  recentGames: Array<{
    key: string;
    map: string;
    endUtc: number;
    won: boolean;
    replayKey: string;
    hasReplay: boolean;
  }>;
}

export type LeaderSort = 'rating' | 'wins' | 'active' | 'recent';
export const LEADER_SORTS: ReadonlyArray<{ id: LeaderSort; label: string }> = [
  { id: 'rating', label: 'Top Rated' },
  { id: 'wins', label: 'Most Wins' },
  { id: 'active', label: 'Most Active' },
  { id: 'recent', label: 'Recent' },
];

export type LoadStatus = 'loading' | 'ready' | 'error';

export interface LeaderboardDeps {
  /** GET seam (the SAME shape as LobbyShellDeps.httpGet) — production: fetch; tests: scripted. */
  httpGet(url: string): Promise<{ body: string | null; error?: string }>;
  /** the view re-renders on every state change. */
  onChange(): void;
  /** base of the JSON endpoints, e.g. 'https://api.warfareincorporated.com/stats' (no trailing slash). */
  baseUrl: string;
  /** the current non-anonymous stats username ('' when anonymous) — scopes the "your rank" pin and
   *  highlights this player's row. Matches buildLeaderboardUrl's `p` (Shell.cpp StatsUsername). */
  playerName: string;
  /** rows to request (default 25, clamped 1..100 server-side). */
  count?: number;
  /** open an external URL (the replay viewer, in a new tab) — production: window.open; tests: a spy.
   *  Absent → window.open when a DOM is present, else a no-op (node tests without a window). */
  openUrl?(url: string): void;
}

export class LeaderboardController {
  status: LoadStatus = 'loading';
  error = '';
  sort: LeaderSort = 'rating';
  rows: LeaderRow[] = [];
  /** the requester's own row when they're outside the visible top N (null otherwise / when anon). */
  me: LeaderRow | null = null;
  /** index into rows() of the highlighted row, or -1. */
  selected = -1;
  /** the player-detail modal: name being shown (null = closed), its profile, and load state. */
  detailName: string | null = null;
  detail: LeaderProfile | null = null;
  detailStatus: LoadStatus = 'loading';

  private readonly d: LeaderboardDeps;
  // monotonically-increasing tokens so a slow earlier fetch can't clobber a newer one (refresh/setSort).
  private listEpoch = 0;
  private detailEpoch = 0;

  constructor(deps: LeaderboardDeps) {
    this.d = deps;
  }

  /** lowercased current player name (for the row highlight); '' when anonymous. */
  private get meName(): string {
    return this.d.playerName.trim().toLowerCase();
  }
  isMe(name: string): boolean {
    return this.meName.length > 0 && name.toLowerCase() === this.meName;
  }

  private listUrl(): string {
    const count = this.d.count ?? 25;
    let url = `${this.d.baseUrl}/leaderboard.json?sort=${this.sort}&count=${count}`;
    const me = this.d.playerName.trim();
    if (me.length > 0) url += `&me=${queryEncode(me)}`;
    return url;
  }

  /** Initial load (or a manual REFRESH): fetch the ranked list for the current sort. */
  async start(): Promise<void> {
    const epoch = ++this.listEpoch;
    this.status = 'loading';
    this.error = '';
    this.d.onChange();
    const req = await this.d.httpGet(this.listUrl());
    if (epoch !== this.listEpoch) return; // a newer request superseded this one
    if (req.body === null) {
      this.status = 'error';
      this.error = req.error ?? 'The leaderboard service is unreachable.';
      this.rows = [];
      this.me = null;
      this.d.onChange();
      return;
    }
    try {
      const obj = JSON.parse(req.body) as { players?: unknown; me?: unknown; sort?: unknown };
      this.rows = Array.isArray(obj.players)
        ? (obj.players as Record<string, unknown>[]).map(toRow)
        : [];
      this.me = obj.me ? toRow(obj.me as Record<string, unknown>) : null;
      this.status = 'ready';
    } catch {
      this.status = 'error';
      this.error = 'The leaderboard data could not be read.';
      this.rows = [];
      this.me = null;
    }
    this.d.onChange();
  }

  refresh(): void {
    void this.start();
  }

  setSort(sort: LeaderSort): void {
    if (sort === this.sort) return;
    this.sort = sort;
    this.selected = -1;
    void this.start();
  }

  select(i: number): void {
    if (i < 0 || i >= this.rows.length) return;
    this.selected = i;
    this.d.onChange();
  }

  /** Open the player-detail modal for `name` and fetch its full profile. */
  openDetail(name: string): void {
    const epoch = ++this.detailEpoch;
    this.detailName = name;
    this.detail = null;
    this.detailStatus = 'loading';
    this.d.onChange();
    void (async () => {
      const req = await this.d.httpGet(`${this.d.baseUrl}/profile.json?u=${queryEncode(name)}`);
      if (epoch !== this.detailEpoch || this.detailName !== name) return; // superseded / closed
      if (req.body === null) {
        this.detailStatus = 'error';
        this.d.onChange();
        return;
      }
      try {
        this.detail = JSON.parse(req.body) as LeaderProfile;
        this.detailStatus =
          this.detail && (this.detail as { error?: string }).error ? 'error' : 'ready';
        if (this.detailStatus === 'error') this.detail = null;
      } catch {
        this.detailStatus = 'error';
        this.detail = null;
      }
      this.d.onChange();
    })();
  }

  /** The watch-replay link for an archived recording — the wi-replay viewer, loaded by archive key. */
  replayUrl(replayKey: string): string {
    return replayViewerUrl(replayKey);
  }

  /** Open the in-browser replay viewer for one of a profile's recent games (no-op without a key). */
  watchReplay(replayKey: string): void {
    openReplayViewer(replayKey, this.d.openUrl);
  }

  closeDetail(): void {
    this.detailEpoch++; // drop any in-flight detail fetch
    this.detailName = null;
    this.detail = null;
    this.detailStatus = 'loading';
    this.d.onChange();
  }
}

function num(v: unknown): number {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}
function toRow(m: Record<string, unknown>): LeaderRow {
  return {
    rank: num(m.rank),
    name: String(m.name ?? ''),
    rating: num(m.rating),
    games: num(m.games),
    wins: num(m.wins),
    winRate: num(m.winRate),
    kd: num(m.kd),
    playtimeSecs: num(m.playtimeSecs),
    lastGameUtc: num(m.lastGameUtc),
    avatar: m.avatar == null ? null : String(m.avatar),
    isBot: m.isBot === true,
  };
}
